import { FileRejection, useDropzone } from 'react-dropzone'
import React, { Fragment, useCallback, useEffect } from 'react'

import { Color } from 'constants/assets'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { QueryStatus } from 'components/common/QueryStatus'
import ReactLoading from 'react-loading'
import classnames from 'classnames'
import styles from './EditorOptionLogoUploader.module.sass'
import { useDraggingFile } from 'utils/hooks/useDraggingFile'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'
import { useVisualsEditing } from 'components/pages/VisualsEditing/VisualsEditing.context'

/**
 * @component EditorOptionLogoUploader for Visuals Editing's Watermark/logo edit option
 * @example
 * <EditorOptionLogoUploader />
 */
export const EditorOptionLogoUploader: React.FC = () => {
  const { t } = useTranslation(['visuals_editing'])
  const { currentUserWorkspace } = useUserData()

  const {
    uploadWatermarkMutation,
    uploadProgress,
    setUploadProgress,
    loadWatermarkLogoQuery
  } = useVisualsEditing()

  const isDraggingFile = useDraggingFile()

  useEffect(() => {
    return () => {
      if (!uploadWatermarkMutation.isPending) {
        uploadWatermarkMutation.reset()
      }
      setUploadProgress(0)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onDrop = useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {

    if (fileRejections && fileRejections.length > 0) {

      // useDropzone onDrop options set to maxFiles: 1
      if (fileRejections.length > 1) {
        alert(`${t('unsupported_upload_max_1_file')}`)
      } else {
        alert(`${t('unsupported_upload')}`)
      }
    }

    for (const file of acceptedFiles) {
      if (currentUserWorkspace) {
        setUploadProgress(0)
        uploadWatermarkMutation.mutate({ workspaceId: currentUserWorkspace.id, file, onProgress: setUploadProgress })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, currentUserWorkspace, setUploadProgress])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: {
      'image/*': ['.png', '.jpeg', '.jpg', '.svg'],
    }, maxFiles: 1
  })

  const isWatermarkLogoUploadingInProgress = uploadWatermarkMutation.isPending && uploadProgress !== 100
  const isWatermarkLoading = loadWatermarkLogoQuery.isLoading

  return (
    <Fragment>
      <div className={classnames('drop-zone', styles.dropZone, { [styles.dragActive]: isDraggingFile, [styles.dragOver]: isDragActive })} {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive || isDraggingFile ?
          <h3>{t('editor_options.drop_here')}</h3>
          :
          (isWatermarkLogoUploadingInProgress || isWatermarkLoading) ?
            <Fragment>
              <ReactLoading
                type="spin"
                color={Color.GRAY_TEXT}
                height="3.6rem"
                width="3.6rem"
              />
              <p className={styles.uploading}>
                {isWatermarkLogoUploadingInProgress ?
                  `${t('editor_options.uploading_file')} ${uploadProgress}%`
                  :
                  t('editor_options.loading')
                }</p>
            </Fragment>
            :
            <Fragment>
              <div className={styles.dragIconContainer}>
                <FileUploadOutlinedIcon className={styles.dragIcon} fontSize='inherit' />
              </div>
              <p className={styles.dragInstructions}>{t('editor_options.drop_instructions')}</p>
              <p className={styles.fileRecommendation}>{t('editor_options.file_recommendation')}</p>
            </Fragment>
        }
      </div>
      <QueryStatus
        query={uploadWatermarkMutation}
        errorMessage={t('error_status_upload_logo')}
        showStates={['error']}
        spaceTopRem={1}
      />
      <QueryStatus
        query={loadWatermarkLogoQuery}
        errorMessage={t('error_status_load_logo')}
        showStates={['error']}
        spaceTopRem={1}
      />
    </Fragment>
  )
}
